import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout, HorizontalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold, TitleNormal } from "../../common/common-text";
import { useState, useEffect } from "react";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import BTEditor from "../../common/components/board/BTEditor";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import { dialogStore } from "../../store/DialogStore";
import {
  requestCreateMarketingDocuments,
  requestUpdateMarketingDocuments,
  requestUploadFiles
} from "../../service/BoardService";
import Marketing from "../../model/board/Marketing";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";

// 오늘 날짜(YYYY-MM-DD)를 서울 시간대 기준으로 계산
const getTodayInSeoul = () => {
  const now = new Date();
  const seoulNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  const y = seoulNow.getFullYear();
  const m = String(seoulNow.getMonth() + 1).padStart(2, '0');
  const d = String(seoulNow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// 날짜 문자열을 UTC+9 오전 9시로 변환하는 함수
const createDateAt9AM = (dateString: string): Date => {
  // UTC+9 시간대에서 오전 9시로 설정
  const date = new Date(dateString + 'T09:00:00+09:00');
  
  return date;
};

const MarketingBoardAdd = () => (
  <MainLayout>
    <MarketingBoardAddBody />
  </MainLayout>
);

export default MarketingBoardAdd;

const MarketingBoardAddBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [contents, setContents] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
  const [marketingType, setMarketingType] = useState('');
  const [postDate, setPostDate] = useState(() => getTodayInSeoul());
  const [isPublished, setIsPublished] = useState(true);
  const [marketingTypeOptions, setMarketingTypeOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const options = location.state;
    if (options) {
        setMarketingTypeOptions(options);
        setMarketingType(options[0].value);
    }
  }, []);

  const handleSave = async () => {
    try {
      baseStore.showLoading();

      const now = new Date();
      const marketing: Marketing = {
        marketingType,
        title,
        representImage: '',
        contents: '',
        postDate: createDateAt9AM(postDate),
        attachedFiles: [],
        isPublished,
        createdBy: authStore.user?.username ?? '',
        createdAt: now,
        updatedBy: authStore.user?.username ?? '',
        updatedAt: now
      };

      const docId = await requestCreateMarketingDocuments(marketing);
      marketing.id = docId;

      // 1) 본문 파싱 및 base64 이미지 수집
      const parser = new DOMParser();
      const docParsed = parser.parseFromString(contents, 'text/html');
      const imgElements = docParsed.querySelectorAll('img');

      const base64ImageSrcs: string[] = [];
      imgElements.forEach((img) => {
        const src = img.getAttribute('src');
        if (src?.startsWith('data:')) base64ImageSrcs.push(src);
      });

      // 2) base64 -> File
      const imagesToUpload: File[] = [];
      for (const base64 of base64ImageSrcs) {
        const res = await fetch(base64);
        const blob = await res.blob();
        imagesToUpload.push(new File([blob], `${Date.now()}_img.png`, { type: blob.type }));
      }

      // 3) 업로드 & 매핑 구성
      const uploadedImageUrls = imagesToUpload.length
        ? await requestUploadFiles('marketing', docId, 'image', imagesToUpload)
        : [];

      // base64 → remote 매핑
      const base64ToRemote = new Map<string, string>();
      base64ImageSrcs.forEach((b64, i) => {
        if (uploadedImageUrls[i]) base64ToRemote.set(b64, uploadedImageUrls[i]);
      });

      // 4) 본문 내 src 치환
      let embedIdx = 0;
      imgElements.forEach((img) => {
        const src = img.getAttribute('src');
        if (src?.startsWith('data:')) {
          const newUrl = uploadedImageUrls[embedIdx++];
          if (newUrl) img.setAttribute('src', newUrl);
        }
      });

      // 5) 첨부파일 업로드
      const uploadedAttachmentUrls = uploadedFiles.length
        ? await requestUploadFiles('marketing', docId, 'attachments', uploadedFiles)
        : [];

      // 6) representImage 결정 (상태값 대신 '즉시' 매핑 사용)
      let representUrl: string | null = null;

      if (selectedImage) {
        if (selectedImage.startsWith('data:')) {
          // data:면 업로드된 URL로 치환
          representUrl = base64ToRemote.get(selectedImage) ?? null;
        } else {
          // 이미 원격이면 그대로 사용
          representUrl = selectedImage;
        }
      }

      // 폴백: 본문 첫 번째 이미지(이미 data: → remote 치환 완료 상태)
      if (!representUrl) {
        representUrl = docParsed.querySelector('img')?.getAttribute('src') ?? null;
      }

      // 7) 최종 저장
      marketing.representImage = representUrl;
      marketing.attachedFiles = uploadedAttachmentUrls;
     
      const imgElements2 = docParsed.querySelectorAll("img");
      imgElements2.forEach((img) => {
        const imgWidth = img.getAttribute("width");
        const imgHeight = img.getAttribute("height");

        if (!imgWidth || !imgHeight) {
          const parentSpan = img.closest("span");
          if (parentSpan) {
            const spanWidth = parentSpan.getAttribute("width");
            const spanHeight = parentSpan.getAttribute("height");

            if (spanWidth) img.setAttribute("width", spanWidth);
            if (spanHeight) img.setAttribute("height", spanHeight);
          }
        }
      });

      // 최종적으로 contents에 반영
      marketing.contents = docParsed.body.innerHTML;

      await requestUpdateMarketingDocuments(marketing);

      dialogStore.showDialog({
        title: '성공',
        message: '홍보자료가 성공적으로 등록되었습니다.',
        onConfirm: () => navigate(-1),
        onClose: () => navigate(-1),
      });
    } catch (e) {
      dialogStore.showDialog({
        title: '오류',
        message: '등록 중 문제가 발생했습니다. 다시 시도해 주세요.',
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
};

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
        <TitleLargeMedium>홍보자료 작성</TitleLargeMedium>
        <BTBox height="24px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

        <VerticalLayout height={'auto'} paddingBottom={36}>
            <TitleMediumBold style={{ marginTop: "20px" }}>홍보 자료 정보</TitleMediumBold>
            <BTBox height="16px" />

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "nowrap",            // 줄바꿈 방지
                    fontSize: "14px",
                    color: "#6C757D",
                }}
            >
                <b style={{ whiteSpace: "nowrap" }}>홍보 유형</b>
                {/* select / date가 100% 먹지 않도록 래퍼로 폭 제어 */}
                <div style={{ width: 260, flex: "0 0 auto" }}>
                    <BTSelectBox
                        width="100%"
                        value={marketingType}
                        options={marketingTypeOptions}
                        onChange={(e) => setMarketingType(e.target.value)}
                      />
                </div>
            </div>

            <BTBox height="16px" />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "nowrap",            // 줄바꿈 방지
                    fontSize: "14px",
                    color: "#6C757D",
                }}
            >
                <b style={{ whiteSpace: "nowrap" }}>게시일자</b>
                <BTDateComponent
                    width="100%"
                    value={postDate}
                    onChange={(value) => setPostDate(value)}
                  />
                <Divider
                    orientation="vertical"
                    sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }}
                />
                <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
                <BTCheckBox
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                />
            </div>
            <BTBox height='16px' />
            <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />
            <TitleMediumBold style={{ marginTop: "20px" }}>홍보자료 컨텐츠 입력</TitleMediumBold>
            <BTBox height='16px' />
            <BTEditor
                title={title}
                contents={contents}
                selectedImage={selectedImage}
                uploadedFiles={uploadedFiles}
                uploadedFileUrls={uploadedFileUrls}
                setTitle={setTitle}
                setContents={setContents}
                setSelectedImage={setSelectedImage}
                setUploadedFiles={setUploadedFiles}
                setUploadedFileUrls={setUploadedFileUrls}
                onSave={handleSave}
                onCancel={() => navigate(-1)}
                attachmentAccept="all"
            />
      </VerticalLayout>
    </VerticalLayout>
  );
});